import { useEffect, useState, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  toEmailJsonb,
  toPhoneJsonb,
} from "@/components/atomic-crm/providers/supabase/contactAdapter";
import { findDuplicateContact, type ContactMatchCandidate } from "./dedup";
import type { SourcePreset } from "./sourceMapping";

interface Props {
  rows: Record<string, string>[];
  mapping: Record<string, { mylesField: string; table: "contact" | "company" }>;
  sourceKey: SourcePreset["key"];
  onComplete: (result: {
    created: number;
    updated: number;
    duplicates: number;
    errors: number;
    conflictCount: number;
    importLogId?: number;
  }) => void;
}

// Fields that exist on the companies table
const VALID_COMPANY_FIELDS = new Set([
  "name",
  "sector",
  "size",
  "linkedin_url",
  "website",
  "phone_number",
  "address",
  "zipcode",
  "city",
  "state_abbr",
  "country",
  "description",
  "revenue",
  "tax_identifier",
  "industry",
  "hq_region",
  "hq_country",
  "business_model",
  "revenue_tier",
  "weltmarkt_ranking",
  "employee_count",
]);

// Fields that exist on the contacts table
const VALID_CONTACT_FIELDS = new Set([
  "first_name",
  "last_name",
  "gender",
  "title",
  "background",
  "first_seen",
  "last_seen",
  "has_newsletter",
  "status",
  "company_id",
  "sales_id",
  "linkedin_url",
  "email_jsonb",
  "phone_jsonb",
  "function_role",
  "seniority_level",
  "tenure_years",
  "tenure_start_date",
  "is_budget_owner",
  "budget_scope",
  "mint_background",
  "innovation_experience",
  "linkedin_activity",
  "social_engagement",
  "owner_id",
  "contact_status",
  "source_origin",
  "current_score",
  "current_tier",
]);

function sanitizeCompanyData(data: Record<string, any>): Record<string, any> {
  const clean: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!VALID_COMPANY_FIELDS.has(key)) continue;
    if (value === "" || value == null) continue;
    // Type coercion for specific fields
    if (key === "employee_count" || key === "revenue_tier" || key === "size") {
      const num = parseInt(String(value), 10);
      if (!isNaN(num)) clean[key] = num;
    } else {
      clean[key] = String(value);
    }
  }
  return clean;
}

function sanitizeContactData(data: Record<string, any>): Record<string, any> {
  const clean: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!VALID_CONTACT_FIELDS.has(key)) continue;
    if (value === "" || value == null) continue;
    if (key === "tenure_years") {
      const num = parseFloat(String(value));
      if (!isNaN(num)) clean[key] = num;
    } else if (key === "is_budget_owner" || key === "has_newsletter") {
      clean[key] = value === true || value === "true" || value === "Yes";
    } else {
      clean[key] = value;
    }
  }
  return clean;
}

export function ImportProgressStep({
  rows,
  mapping,
  sourceKey,
  onComplete,
}: Props) {
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({
    created: 0,
    updated: 0,
    duplicates: 0,
    errors: 0,
    conflicts: 0,
  });
  const [lastError, setLastError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    runImport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function runImport() {
    // Create import log
    const { data: importLog } = await supabase
      .from("import_logs")
      .insert({
        filename: "import.csv",
        source_type: sourceKey,
        column_mapping: mapping,
        row_count: rows.length,
        status: "processing",
      })
      .select()
      .single();

    // Load existing contacts for dedup
    const { data: existingContacts } = await supabase
      .from("contacts")
      .select("id, first_name, last_name, email_jsonb, company_id");

    // Load existing companies for name lookup
    const { data: existingCompanies } = await supabase
      .from("companies")
      .select("id, name");

    const companyCache: Record<string, number> = {};
    (existingCompanies || []).forEach((c: any) => {
      companyCache[c.name.toLowerCase()] = c.id;
    });

    const candidates: ContactMatchCandidate[] = (existingContacts || []).map(
      (c: any) => ({
        id: c.id,
        email: c.email_jsonb?.[0]?.email || null,
        first_name: c.first_name || "",
        last_name: c.last_name || "",
        company_name: "", // We'll match by email/name primarily
      }),
    );

    let created = 0,
      updated = 0,
      duplicates = 0,
      errors = 0,
      conflicts = 0;

    for (let i = 0; i < rows.length; i++) {
      try {
        const row = rows[i];
        const contactData: Record<string, any> = {};
        const companyData: Record<string, any> = {};

        // Map CSV columns to MYLES fields
        for (const [csvCol, { mylesField, table }] of Object.entries(mapping)) {
          const value = row[csvCol];
          if (!value || value.trim() === "") continue;
          if (table === "contact") contactData[mylesField] = value.trim();
          else companyData[mylesField] = value.trim();
        }

        // Handle Pipedrive full_name split
        if (contactData.full_name) {
          const parts = contactData.full_name.split(" ");
          contactData.first_name = parts[0];
          contactData.last_name = parts.slice(1).join(" ");
          delete contactData.full_name;
        }

        // Skip rows with no name
        if (!contactData.first_name && !contactData.last_name) {
          errors++;
          continue;
        }

        // Resolve company (find or create)
        let companyId: number | null = null;
        if (companyData.name) {
          const cached = companyCache[companyData.name.toLowerCase()];
          if (cached) {
            companyId = cached;
          } else {
            const cleanCompany = sanitizeCompanyData(companyData);
            const { data: newCompany, error: companyError } = await supabase
              .from("companies")
              .insert(cleanCompany)
              .select("id")
              .single();

            if (companyError) {
              console.error(
                `Company create error for "${companyData.name}":`,
                companyError,
              );
              setLastError(
                `Row ${i + 1}: Company "${companyData.name}" - ${companyError.message}`,
              );
              // Try without the problematic fields - just name
              const { data: fallbackCompany } = await supabase
                .from("companies")
                .insert({ name: companyData.name })
                .select("id")
                .single();
              companyId = fallbackCompany?.id || null;
            } else {
              companyId = newCompany?.id || null;
            }
            if (companyId) {
              companyCache[companyData.name.toLowerCase()] = companyId;
            }
          }
        }

        // Extract email/phone before sanitizing (they need jsonb conversion)
        const email = contactData.email;
        const phone = contactData.phone;
        delete contactData.email;
        delete contactData.phone;
        // Remove fields that aren't DB columns
        delete contactData.connected_on;

        const cleanContact = sanitizeContactData({
          ...contactData,
          company_id: companyId,
          source_origin: sourceKey,
          contact_status: "new",
        });
        if (email) cleanContact.email_jsonb = toEmailJsonb(email);
        if (phone) cleanContact.phone_jsonb = toPhoneJsonb(phone);

        // Dedup check
        const incoming = {
          email: email || "",
          first_name: contactData.first_name || "",
          last_name: contactData.last_name || "",
          company_name: companyData.name || "",
        };
        const duplicate = findDuplicateContact(incoming, candidates);

        if (duplicate) {
          duplicates++;
          const { data: existingRecord } = await supabase
            .from("contacts")
            .select("*")
            .eq("id", duplicate.id)
            .single();
          if (existingRecord) {
            const updates: Record<string, any> = {};
            for (const [key, value] of Object.entries(cleanContact)) {
              if (
                value != null &&
                (existingRecord[key] == null || existingRecord[key] === "")
              ) {
                updates[key] = value;
              }
            }
            if (Object.keys(updates).length > 0) {
              await supabase
                .from("contacts")
                .update(updates)
                .eq("id", duplicate.id);
              updated++;
            }
          }
        } else {
          const { data: newContact, error: contactError } = await supabase
            .from("contacts")
            .insert(cleanContact)
            .select("id")
            .single();
          if (contactError) {
            console.error(
              `Contact create error for "${contactData.first_name} ${contactData.last_name}":`,
              contactError,
            );
            setLastError(
              `Row ${i + 1}: ${contactData.first_name} ${contactData.last_name} - ${contactError.message}`,
            );
            errors++;
          } else if (newContact) {
            created++;
            candidates.push({
              id: newContact.id,
              email: email || null,
              first_name: contactData.first_name || "",
              last_name: contactData.last_name || "",
              company_name: companyData.name || "",
            });
          }
        }
      } catch (err: any) {
        console.error(`Import row ${i + 1} error:`, err);
        setLastError(`Row ${i + 1}: ${err.message}`);
        errors++;
      }
      setProgress(((i + 1) / rows.length) * 100);
      setStats({ created, updated, duplicates, errors, conflicts });
    }

    // Update import log
    if (importLog) {
      await supabase
        .from("import_logs")
        .update({
          created_count: created,
          updated_count: updated,
          duplicate_count: duplicates,
          error_count: errors,
          status: errors > 0 && created === 0 ? "failed" : "completed",
        })
        .eq("id", importLog.id);
    }

    onComplete({
      created,
      updated,
      duplicates,
      errors,
      conflictCount: conflicts,
      importLogId: importLog?.id,
    });
  }

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold">Importing...</h2>
      <Progress value={progress} className="mb-4" />
      <p className="text-sm text-muted-foreground">
        {Math.round(progress)}% complete
      </p>
      {lastError && (
        <p className="mt-2 rounded border border-orange-300 bg-orange-50 p-2 text-xs text-orange-800 dark:bg-orange-950 dark:text-orange-200">
          Last issue: {lastError}
        </p>
      )}
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-md bg-green-50 p-3 text-center dark:bg-green-950">
          <div className="text-2xl font-bold text-green-600">
            {stats.created}
          </div>
          <div className="text-xs text-muted-foreground">Created</div>
        </div>
        <div className="rounded-md bg-blue-50 p-3 text-center dark:bg-blue-950">
          <div className="text-2xl font-bold text-blue-600">
            {stats.updated}
          </div>
          <div className="text-xs text-muted-foreground">Updated</div>
        </div>
        <div className="rounded-md bg-yellow-50 p-3 text-center dark:bg-yellow-950">
          <div className="text-2xl font-bold text-yellow-600">
            {stats.duplicates}
          </div>
          <div className="text-xs text-muted-foreground">Duplicates</div>
        </div>
        <div className="rounded-md bg-red-50 p-3 text-center dark:bg-red-950">
          <div className="text-2xl font-bold text-red-600">{stats.errors}</div>
          <div className="text-xs text-muted-foreground">Errors</div>
        </div>
      </div>
    </div>
  );
}
